import React, {useState, useEffect, forwardRef, memo, useMemo, useCallback, useRef} from "react";
import classnames from 'classnames';
import {AgGridReact} from "ag-grid-react";
import {CellClickedEvent, CellEditRequestEvent, ColDef, ColumnApi, ColumnMovedEvent, ColumnPinnedEvent, ColumnResizedEvent, ColumnState, FilterChangedEvent, GridApi, GridReadyEvent, SortChangedEvent} from "ag-grid-community";
import "ag-grid-community/styles/ag-grid.min.css"; // Move up to top of app?  eg. app.js or index.js?
import 'ag-grid-community/styles/ag-theme-alpine.css'; // Move up to top of app?  eg. app.js or index.js?
// must import this custom CSS last
import './ByzzerTable.scss';
import {AgGridReactProps} from "ag-grid-react/lib/shared/interfaces";
import { useTenantApi } from "@/hooks";
import { useUser } from "@/contexts/UserContext";
import { debounce } from "lodash";
import { ByzzerButton, ByzzerCheckbox } from "@byzzer/ui-components";
import { ByzColDef, ByzColGroupDef, ByzColumnDef, TableArea } from "@/types/TableTypes";
import _ from "lodash";
import { ByzzerMenu } from '@/components/ByzzerMenu';

const baseClassName = 'byz-table';

export const BYZZER_TABLE_THEME = 'ag-theme-alpine';

const LAYOUT_SAVE_DEBOUNCE_TIME_IN_MS = 500;

type ByzzerTableBaseProps = {
    baseColumnWidth?: number;
    autoHeight?: boolean;
    suppressMenu?: boolean;
    wrapText?: boolean;
    headerClassName?: string;
    enableColumnHideShow?: boolean;
    enableSaveSort?: boolean;
    factTriggerRef?: React.RefObject<HTMLElement>;
    onColumnVisibilityChange?: (colId: string, visible: boolean) => void;
}

type ByzzerTableWithSave<TData, TColId> = {
    enableSaveLayout: true;
    columnDefs: ByzColumnDef<TData, TColId>[];
    tableArea: TableArea;
}

type ByzzerTableWithoutSave<TData, TColId> = {
    enableSaveLayout?: false;
    columnDefs: ByzColumnDef<TData, TColId>[] | ColDef<TData>[];
    tableArea?: TableArea;
}

type ByzzerTableProps<TData, TColId, TContext> = (
    ByzzerTableBaseProps
    & (ByzzerTableWithSave<TData, TColId> | ByzzerTableWithoutSave<TData, TColId>) 
    & Partial<Omit<AgGridReactProps<TData>, 'columnDefs'>>
    & {
        uncheckedColumns?: NonNullable<ByzColDef<TData, TColId>['colId']>[];
    }
);

function isColumnGroup<TData, TColId>(column: ByzColumnDef<TData, TColId>): column is ByzColGroupDef<TData, TColId> {
    return 'children' in column;
}

function getLeafColumns<TData, TColId>(columns: ByzColumnDef<TData, TColId>[]): ByzColDef<TData, TColId>[] {
    return columns.flatMap((column) => isColumnGroup(column) ? getLeafColumns(column.children) : column);
}

const ByzzerTableInner = <TData, TColId, TValue, TContext>({
    rowData,
    columnDefs = [],
    baseColumnWidth = 350,
    defaultColDef,
    autoHeight = true,
    suppressMenu = true,
    wrapText = true,
    onCellClicked,
    readOnlyEdit = false,
    onCellEditRequest,
    onFilterChanged,
    onGridReady,
    enableColumnHideShow,
    enableSaveLayout,
    suppressClickEdit,
    singleClickEdit,
    tableArea,
    headerClassName,
    suppressMovableColumns = false,
    enableSaveSort = false,
    factTriggerRef,
    uncheckedColumns,
    onColumnVisibilityChange,
    ...props
}: ByzzerTableProps<TData, TColId, TContext>, gridRef: React.Ref<AgGridReact<TData>>) => {
    const gridApiRef = useRef<GridApi | null>(null);
    const columnApiRef = useRef<ColumnApi | null>(null);
    const gridReadyRef = useRef<boolean>(false);
    const triggerRef = factTriggerRef ? factTriggerRef : useRef<HTMLDivElement>(null);
    const initialColumnStateRef = useRef<ColumnState[]>();

    const [gridFullyInitialized, setGridFullyInitialized] = useState<boolean>(false); // should only be set to true once the grid is ready and the user's column state has been applied

    const [gridReadyData, setGridReadyData] = useState<GridReadyEvent<TData, TContext>>();
    const [checkBoxVisibility, setCheckBoxVisibility] = useState<{ [key: string]: boolean }>({}); // Initialize state for column visibility based on dynamic columnDefs
    
    const {saveTableLayout} = useTenantApi();
    const {refreshUser, user} = useUser();

    const recalculateCheckBoxVisibility = useCallback(() => {
        if (!columnApiRef.current || !tableArea) return;

        const columnState = columnApiRef.current.getColumnState();
        const userLayouts: Record<TableArea, ColumnState[] | null> | undefined = user?.metadata?.tableLayouts;

        // Function to build the checkbox visibility map
        const buildCheckboxStates = (states: ColumnState[]) => {
            const result: Record<string, boolean> = {};
            for (const state of states) {
                result[state.colId] = !state.hide;
            }
            return result;
        };

        const applyUncheckedColumns = (state: ColumnState[]) => {
            const newState = state.map((col) => ({
                ...col,
                hide: uncheckedColumns?.includes(col.colId as any) || col.hide,
            }));

            columnApiRef.current!.applyColumnState({ state: newState });

            const checkboxStates = newState.reduce((acc, col) => {
                acc[col.colId] = uncheckedColumns?.includes(col.colId as any) ? false : !col.hide;
                return acc;
            }, {} as Record<string, boolean>);

            setCheckBoxVisibility(checkboxStates);
            triggerLayoutChange();
        };

        if (userLayouts) {
            const layoutExists = Object.prototype.hasOwnProperty.call(userLayouts, tableArea);
            const userColumnState = userLayouts[tableArea] ?? [];

            if (layoutExists) {
                const hasMatch = userColumnState.some((obj: any) => uncheckedColumns?.includes(obj.colId));

                if (!hasMatch) {
                    applyUncheckedColumns(columnState);
                } else {
                    setCheckBoxVisibility(buildCheckboxStates(columnState));
                }
            } else {
                applyUncheckedColumns(columnState);
            }
        } else {
            applyUncheckedColumns(columnState);
        }
    }, []);

    useEffect(() => {
        setGridReadyData(undefined);
        setGridFullyInitialized(false);
    }, [tableArea]);

    useEffect(() => {
        if (gridReadyData) {
            gridReadyRef.current = true;
            if (enableSaveLayout && !gridFullyInitialized) {
                applyUserSavedState(gridReadyData);
            }
            setGridFullyInitialized(true);
        }
    }, [gridReadyData]);

    useEffect(() => {
        if (gridFullyInitialized && gridReadyData) {
            onGridReady?.(gridReadyData);
        }
    }, [gridFullyInitialized]);

    useEffect(() => {
        if (gridReadyRef.current) {
            recalculateCheckBoxVisibility();
        }
    }, [columnDefs, gridFullyInitialized, recalculateCheckBoxVisibility]);
    

    const clearSort = (col: ColumnState) => {
        if (!col || enableSaveSort) return col;
        return {
            ...col,
            sort: null,
            sortIndex: null
        };
    }

    // Function to toggle column visibility
    const handleCheckBoxVisibilityChange = (field, checked) => {
        setCheckBoxVisibility((prevVisibility) => {
            const updatedVisibility = { ...prevVisibility, [field]: checked };

            // Get column state using the new API
            const columnState = columnApiRef.current?.getColumnState();

            // Update the column state based on the new visibility
            const newColumnState = columnState?.map((state) => {
                if (state.colId === field) {
                    return { ...state, hide: !checked };
                }
                return state;
            });

            // Apply the updated column state
            columnApiRef.current?.applyColumnState({ state: newColumnState });
            onColumnVisibilityChange?.(field, checked);
            triggerLayoutChange();
            return updatedVisibility;
        });
    };

    // Render checkboxes for column visibility control
    const rendercheckBoxVisibilityControls = () => (
        <>
            {getLeafColumns(columnDefs as ByzColumnDef<TData, TColId>[])
                .filter((column: any) => (column.headerName && (!column.disableHide)))
                .map((column: any) => (
                    <ByzzerCheckbox
                        key={`{${column.colId}_${column.headerName}`}
                        className={`${baseClassName}__columns-checkbox`}
                        checked={checkBoxVisibility[column?.colId!] ?? false}
                        label={column.headerName}
                        onChange={(e: any) => handleCheckBoxVisibilityChange(column.colId, e.checked)}
                    />
                ))}
        </>
    );

    const applyUserSavedState = (gridReadyData: GridReadyEvent<TData, TContext>) => {
        if (!tableArea) return;
        const userLayouts: Record<TableArea, ColumnState[] | null> | undefined = user?.metadata?.tableLayouts;
        const userColumnState = userLayouts?.[tableArea]?.map(clearSort) ?? [];

        if (Boolean(userColumnState) && enableSaveLayout) {
            const applyResult = gridReadyData.columnApi.applyColumnState({state: userColumnState, applyOrder: enableSaveSort}); //BYZ-15636 applyOrder in ByzzerTable component should be driven by enableSaveSort prop passed by the parent
            if (applyResult && userColumnState.length) {
                // Update the checkbox states based on the column visibility
                const updatedCheckboxStates = userColumnState.reduce((acc, state) => {
                  acc[state.colId] = state.hide !== true; // If hide is true, the column is hidden
                    return acc;
                }, {});
                setCheckBoxVisibility(updatedCheckboxStates);
            }
        } else {
            console.warn(`Could not apply columns for tableArea: ${tableArea}. `, {enableSaveLayout, userColumnState: userColumnState});
        }
    }

    const columnDefsMemo = useMemo(() => {
        const normalizeHeaders = (columns: ByzColumnDef<TData, TColId>[]): ByzColumnDef<TData, TColId>[] => columns.map((column) => {
            if (isColumnGroup(column)) {
                return {
                    ...column,
                    children: normalizeHeaders(column.children),
                };
            }
            return {
                ...column,
                headerName: column.headerName ?? '', // overriding default that displays the field name as header name
            };
        });

        return normalizeHeaders(columnDefs as ByzColumnDef<TData, TColId>[]);
    }, [columnDefs, enableColumnHideShow]);

    const baseColDef: Partial<ColDef<TData>> = useMemo(() => ({
        width: baseColumnWidth,  // set every column width
        filter: true,  // make every column use 'text' filter by default
        sortable: true,  // enable floating filters by default
        floatingFilter: true,
        resizable: true,
        suppressMenu,
        autoHeight,
        wrapText,
        cellStyle: {
            wordBreak: 'normal'
        },
        lockVisible: true,
        wrapHeaderText: true,
        autoHeaderHeight: true,

        // https://stackoverflow.com/questions/33894190/how-to-wordwrap-a-header-in-ag-grid

    }), [baseColumnWidth, suppressMenu, autoHeight, wrapText, columnDefs, enableColumnHideShow]); // columnDefsMemo

    const handleCellClick = useCallback((event: CellClickedEvent<TData, TValue>) => {
        onCellClicked?.(event);
    }, [onCellClicked, gridFullyInitialized]);

    const handleCellEditRequest = useCallback((event: CellEditRequestEvent<TData, TValue>) => {
        onCellEditRequest?.(event);
    }, [onCellEditRequest, gridFullyInitialized])

    const handleFilterChanged = useCallback((event: FilterChangedEvent<TData, TContext>) => {
        onFilterChanged?.(event);
    }, [onFilterChanged, gridFullyInitialized]);

    const handleGridReady = useCallback((event: GridReadyEvent<TData, TContext>) => {
        gridApiRef.current = event.api;
        columnApiRef.current = event.columnApi;
        initialColumnStateRef.current = event.columnApi.getColumnState();
        setGridReadyData(event);
    }, [onGridReady]);

    const commitTableLayout = async (source?: string, columnState?: ColumnState[]) => {
        const currentColumnState = columnApiRef.current?.getColumnState() ?? null;

        if (enableSaveLayout && Boolean(tableArea)) {
            try {
                const colStateToSave = (columnState ?? currentColumnState)?.map(clearSort) ?? null;
                saveTableLayout({ tableArea, columnState: colStateToSave });
                refreshUser({
                    ...user,
                    metadata: {
                        ...user?.metadata,
                        tableLayouts: {
                            ...user?.metadata?.tableLayouts,
                            [tableArea!]: source === 'reset' ? null : colStateToSave
                        }
                    }
                });
            } catch (error) {
                console.error(`Error saving table layout: ${error}`);
            }
        }
    }

    const debouncedCommitTableLayout = useCallback(debounce(
        async (source?: string, columnState?: ColumnState[]) => {
            if (enableSaveLayout && Boolean(tableArea)) {
                commitTableLayout(source, columnState); // might not need to await?
            }
        },
        LAYOUT_SAVE_DEBOUNCE_TIME_IN_MS
    ), [commitTableLayout]);

    function triggerLayoutChange(source?: string, columnState?: ColumnState[]) {
        if (enableSaveLayout && Boolean(tableArea)) {
            debouncedCommitTableLayout.cancel();
            debouncedCommitTableLayout(source, columnState);
        }
    }

    const handleColumnMoved = useCallback(async (event: ColumnMovedEvent<TData, TContext>) => {
        if (!gridFullyInitialized || !gridReadyRef.current || !event.finished || event.source === 'flex') return;
        triggerLayoutChange();
    }, [gridFullyInitialized]);

    const handleColumnResized = useCallback(async (event: ColumnResizedEvent<TData, TContext>) => {
        if (!gridFullyInitialized || !gridReadyRef.current || !event.finished || event.source === 'flex') return;
        triggerLayoutChange();
    }, [gridFullyInitialized]);

    const handleColumnPinned = useCallback(async (event: ColumnPinnedEvent<TData, TContext>) => {
        if (!gridFullyInitialized || !gridReadyRef.current) return;
        triggerLayoutChange();
    }, [gridFullyInitialized]);

    const handleSortChange = useCallback(async (event: SortChangedEvent<TData, TContext>) => {
        if (!gridFullyInitialized || !gridReadyRef.current) return;
        triggerLayoutChange();
    }, [gridFullyInitialized]);

    const handleReset = () => {
        if (columnApiRef.current) {
            const columnStateBeforeReset = columnApiRef.current?.getColumnState();

            columnApiRef.current.resetColumnState();

            const sortStateBeforeReset = columnStateBeforeReset
                .filter(col => col.sort)
                .map((col) => ({
                    colId: col.colId,
                    sort: col.sort,
                    sortIndex: col.sortIndex,
                }));

            const columnStateAfterReset = columnApiRef.current?.getColumnState();

            const resetColsWithPriorSortState: ColumnState[] = columnStateAfterReset?.map((resetCol) => {
                if (enableSaveSort) return resetCol;
                const sortStateForResetCol = sortStateBeforeReset.find((sortCol) => sortCol.colId === resetCol.colId);
                return {
                    ...resetCol,
                    ...sortStateForResetCol
                }
            })
            const applyOrder = enableSaveSort;
            columnApiRef.current?.applyColumnState({
                state: resetColsWithPriorSortState,
                applyOrder
            });

            recalculateCheckBoxVisibility(); // Reinitialize checkboxes
            triggerLayoutChange('reset');
        }
    }

    const toComparableColumns = useCallback((col: ColumnState) => {
        const { flex = null, width, sort, sortIndex, ...rest } = col; // maybe pinned too?
        const sortConfig =  enableSaveSort ? {sort, sortIndex} : {};
        if (suppressMovableColumns || flex) {
            return {...sortConfig,...rest};
        }
        return { flex, width, ...sortConfig, ...rest };
    }, [suppressMovableColumns]);

    const columnStatesAreSame = (state1: ColumnState[], state2: ColumnState[]) => {
        const state1Filtered = state1.map(toComparableColumns);
        const state2Filtered = state2.map(toComparableColumns);

        return (
            state1Filtered.length === state2.length &&
            _.isEqual(state1Filtered, state2Filtered)
        );
    }

    const isDefaultLayout = Boolean(initialColumnStateRef.current) && Boolean(columnApiRef.current?.getColumnState()) && columnStatesAreSame(initialColumnStateRef.current!, columnApiRef.current?.getColumnState()!)
    const showReset = !isDefaultLayout;
    const gridKey = useMemo(() => `grid-${tableArea}`, [tableArea]);

    return (
        <div className={classnames(`${baseClassName}__wrapper`)}>
            {enableColumnHideShow && (
                <div 
                    className={classnames(`${baseClassName}__column-panel`, `${baseClassName}__column-panel-options`, {
                        [`${baseClassName}__column-panel-options--has-multiple-items`]: showReset
                    })}
                >
                    {showReset && (
                        <ByzzerButton onClick={handleReset}>Reset columns</ByzzerButton>
                    )}
                    <span ref={triggerRef} className={classnames(`${baseClassName}__columns-menu`)}>Columns</span>
                </div>
            )}
            <ByzzerMenu
                items={[]}
                content={rendercheckBoxVisibilityControls()}
                reference={triggerRef}
                placement={'bottom-end'}
                offset={[0, 2]}
                triggerTarget={triggerRef.current}
            />
            <AgGridReact<TData>
                key={gridKey}
                className={BYZZER_TABLE_THEME}
                ref={gridRef} // Ref for accessing Grid's API
                rowData={rowData} // Row Data for Rows
                columnDefs={columnDefsMemo as any} // Column Defs for Columns
                defaultColDef={defaultColDef ?? baseColDef} // Default Column Properties
                enableCellTextSelection={true}
                tooltipShowDelay={1500}
                // tooltipHideDelay={2500}
                suppressMenuHide={true}
                onCellClicked={handleCellClick} //we probably need this soon in future for further enhancements
                readOnlyEdit={readOnlyEdit}
                onCellEditRequest={handleCellEditRequest}
                singleClickEdit={singleClickEdit}
                suppressClickEdit={suppressClickEdit} //set this true if you only want to have your own way of starting editing, such as clicking a button in your custom cell renderer. setting this true will prevent cell editing
                suppressScrollOnNewData={true}  // tells the grid to NOT scroll to the top when the page changes.
                onGridReady={handleGridReady}
                onColumnMoved={handleColumnMoved}
                onColumnPinned={handleColumnPinned}
                onColumnResized={handleColumnResized}
                onSortChanged={handleSortChange}
                // onColumnVisible={handleColumnVisible}
                onFilterChanged={handleFilterChanged}
                suppressMovableColumns={suppressMovableColumns}
                {...props}
            />
        </div>
    );
};

export const ByzzerTable = memo(forwardRef(ByzzerTableInner)) as <TData, TColId, TContext>(
    props: ByzzerTableProps<TData, TColId, TContext> & React.RefAttributes<AgGridReact<TData>>
) => JSX.Element;

// @ts-ignore
ByzzerTable.displayName = 'ByzzerTable';
